/**
 * Générateur — Déterminer si une expression littérale est une somme ou un
 * produit.
 * Attendu ID : 935 | Niveau : 5ème | Domaine : Nombres et calculs / Calcul littéral
 *
 * Plan de cartes (mise en conformité avec la spécification 935, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Somme ou produit ? La dernière opération décide » (id nature)
 *     nature_classer : deux catégories, somme et produit, et deux seulement.
 *     nature_parmi_qcm_unique : quatre expressions, une seule de la nature
 *       demandée — c'est la forme fermée possible ici, la liste des natures
 *       n'ayant que deux entrées (voir « non tranché »).
 *
 *   Carte « La fiche d'identité d'une expression » (id fiche)
 *     fiche_texte_trou_drag : la nature, la DERNIÈRE opération à effectuer, et
 *       le nombre de termes ou de facteurs. Aucun calcul n'est demandé.
 *
 *   Carte « Vrai ou faux : somme ou produit » (id vf)
 *     vf_tableau_vf : quatre affirmations, dont celles qui portent les erreurs
 *       « la nature dépend de la valeur » et « la dernière opération est la
 *       première à effectuer ».
 *     vf_qcm_unique : réécrire correctement une affirmation fausse.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\935.json) :
 *   Bornes appliquées : DEUX natures, somme et produit (borne `prescrit`) ;
 *   1 à 4 opérations ; 1 ou 2 lettres, tirées dans x, y, z, t, n, m ;
 *   coefficients entiers 2..10, ou décimaux 2,1..9,9 dans le réglage
 *   transverse ; signes de multiplication affichés aux niveaux 1 et 2, masqués
 *   à partir du niveau 3 ; aucune parenthèse imbriquée.
 *   Exclusions appliquées : aucune expression dont la DERNIÈRE opération serait
 *   une soustraction ou une division (le niveau 5 est désactivé par défaut) ;
 *   aucun coefficient valant 1 ; aucun calcul demandé, la réponse est une
 *   nature ; aucune juxtaposition entre deux nombres ; aucune expression
 *   produite deux fois dans une même carte.
 *   Erreurs visées → mots-clés :
 *     nature lue sur la première opération   → premiere_operation_lue
 *     a + bx pris pour un produit            → somme_prise_pour_produit
 *     a(b + x) pris pour une somme           → produit_pris_pour_somme
 *     dernière opération et première confondues → derniere_et_premiere_operation
 *     nature crue dépendante de la valeur    → nature_dependante_de_la_valeur
 *     produit par juxtaposition non reconnu  → juxtaposition_non_reconnue
 *     lecture guidée par la place des lettres → position_des_lettres
 *   Graduation → cartes : 1 (une opération, signes affichés) et 2 (deux
 *   opérations, l'exemple du programme) → nature_*, vf_* ; 3 (signes masqués)
 *   → nature_*, fiche_texte_trou_drag, vf_* ; 4 (trois ou quatre opérations,
 *   deux lettres) → nature_*, fiche_texte_trou_drag ; 5 → aucune carte, la spec
 *   le dit « désactivé par défaut et hors du périmètre du programme » ; réglage
 *   transverse (nombres décimaux) → toutes les cartes de la rubrique nature.
 *   Non tranché : le `type_reponse` décrit une liste fermée à DEUX entrées plus
 *   une entrée vide ; le harnais interdit un QCM de moins de trois propositions
 *   (S-QCM-MIN3). La question fermée porte donc sur quatre EXPRESSIONS dont une
 *   seule a la nature demandée, au lieu de porter sur deux natures.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeClasser, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 935;

const L_NATURE = 'Somme ou produit ? La dernière opération décide';
const L_FICHE = 'La fiche d\'identité d\'une expression';
const L_VF = 'Vrai ou faux : somme ou produit';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage et étiquettes, jamais en saisie)

/**
 * Une lettre de variable s'écrit en italique mathématique, jamais en lettre
 * ordinaire (règle d'équipe, retour prof 2026-09-24). Deux rendus, deux écritures :
 *   - ÉNONCÉ (`payload.texte`) : la regex de rendu est STRICTE, le fragment doit
 *     commencer par une commande LaTeX → `tex()` pose le `\mathrm{}` de largeur nulle ;
 *   - ÉTIQUETTES à classer, CHOIX de QCM, lignes de vrai/faux : regex PERMISSIVE →
 *     le « $x$ » nu suffit, et `\mathrm{}` y est interdit.
 *
 * `enLatex` traduit notre écriture d'AFFICHAGE en LaTeX : sans elle KaTeX espacerait
 * la virgule décimale comme une ponctuation (« 4, 3 ») et lirait mal le moins
 * typographique.
 *
 * ⚠️ Les expressions restent BRUTES dans `_meta.params` : elles y sont relues
 * opérateur par opérateur par les tests de conformité. On ne convertit qu'à
 * l'AFFICHAGE, au dernier moment.
 */
const enLatex = (s) => String(s)
    .replace(/−/g, '-')
    .replace(/\s*×\s*/g, ' \\times ')
    .replace(/(\d),(\d)/g, '$1{,}$2');
const mathEnonce = (s) => tex(enLatex(s));
const mathChoix = (s) => `$${enLatex(s)}$`;

// ─── Bornes de la spécification ─────────────────────────────────────────
// a, b et c sont écartées : elles désignent par convention des coefficients.
const LETTRES = ['x', 'y', 'z', 't', 'n', 'm'];
const COEF_MIN = 2;
const COEF_MAX = 10;
const VALEUR_ILLUSTRATIVE_MIN = 2;
const VALEUR_ILLUSTRATIVE_MAX = 9;

/**
 * Un coefficient AFFICHÉ : entier 2..10 (`variables.coefficient`), ou décimal
 * 2,1..9,9 (`variables.coefficient_decimal`, réglage transverse). Jamais 1
 * (exclusions 3), virgule décimale française.
 */
const coefTexte = (decimal) => {
    if (!decimal) return String(randInt(COEF_MIN, COEF_MAX));
    return String(randInt(21, 99) / 10).replace('.', ',');
};

// ═════════ Familles d'expressions (la nature vient de la construction) ═════════

/**
 * Chaque famille connaît sa nature, son niveau, son nombre d'opérations, et le
 * mot-clé de l'erreur qu'elle sert quand elle joue le rôle de distracteur.
 * La DERNIÈRE opération à effectuer est toujours une addition (somme) ou une
 * multiplication (produit) : jamais une soustraction ni une division
 * (exclusions 1, le niveau 5 restant désactivé).
 */
const FAMILLES = [
    // niveau 1 : une seule opération, signes de multiplication affichés
    {
        id: 'sommeSimple', nature: 'somme', niveau: 1, nbOperations: 1, lettres: 1,
        signes: true, code: 'premiere_operation_lue',
        txt: (L, k) => `${L} + ${k}`,
    },
    {
        id: 'produitSimple', nature: 'produit', niveau: 1, nbOperations: 1, lettres: 1,
        signes: true, code: 'produit_pris_pour_somme',
        txt: (L, k) => `${k} × ${L}`,
    },
    // niveau 2 : deux opérations, signes affichés — l'exemple du programme
    {
        id: 'sommeCoefAffiche', nature: 'somme', niveau: 2, nbOperations: 2, lettres: 1,
        signes: true, code: 'somme_prise_pour_produit',
        txt: (L, k, a) => `${k} × ${L} + ${a}`,
    },
    {
        id: 'produitParenthAffiche', nature: 'produit', niveau: 2, nbOperations: 2, lettres: 1,
        signes: true, code: 'produit_pris_pour_somme',
        txt: (L, k, a) => `${k} × (${L} + ${a})`,
    },
    // niveau 3 : deux opérations, produits écrits par juxtaposition
    {
        id: 'sommeCoef', nature: 'somme', niveau: 3, nbOperations: 2, lettres: 1,
        signes: false, code: 'juxtaposition_non_reconnue',
        txt: (L, k, a) => `${k}${L} + ${a}`,
    },
    {
        id: 'produitParenth', nature: 'produit', niveau: 3, nbOperations: 2, lettres: 1,
        signes: false, code: 'produit_pris_pour_somme',
        txt: (L, k, a) => `${k}(${L} + ${a})`,
    },
    {
        id: 'sommeDeuxCoef', nature: 'somme', niveau: 3, nbOperations: 3, lettres: 1,
        signes: false, code: 'somme_prise_pour_produit',
        txt: (L, k, a) => `${k}${L} + ${a}${L}`,
    },
    {
        id: 'produitParenthMoins', nature: 'produit', niveau: 3, nbOperations: 2, lettres: 1,
        signes: false, code: 'produit_pris_pour_somme',
        txt: (L, k, a) => `${k}(${L} ${MOINS} ${a})`,
    },
    // niveau 4 : trois ou quatre opérations, deux lettres, positions variables
    {
        id: 'produitDeuxParenth', nature: 'produit', niveau: 4, nbOperations: 3, lettres: 2,
        signes: false, code: 'produit_pris_pour_somme',
        txt: (L, k, a, M) => `(${L} + ${k})(${M} + ${a})`,
    },
    {
        id: 'sommeDeuxLettres', nature: 'somme', niveau: 4, nbOperations: 3, lettres: 2,
        signes: false, code: 'position_des_lettres',
        txt: (L, k, a, M) => `${k}${L} + ${a}${M}`,
    },
    {
        id: 'produitCoefParenth', nature: 'produit', niveau: 4, nbOperations: 3, lettres: 2,
        signes: false, code: 'position_des_lettres',
        txt: (L, k, a, M) => `${k}${L}(${M} + ${a})`,
    },
    {
        id: 'sommeParenthPlus', nature: 'somme', niveau: 4, nbOperations: 3, lettres: 2,
        signes: false, code: 'premiere_operation_lue',
        txt: (L, k, a, M) => `${k}(${L} + ${a}) + ${M}`,
    },
];

/** Une expression tirée dans une famille. Deux lettres toujours distinctes. */
const tirerExpr = (famille, decimal) => {
    const [L, M] = shuffle(LETTRES).slice(0, 2);
    const k = coefTexte(decimal);
    let a = coefTexte(decimal);
    if (a === k) a = coefTexte(decimal);
    return {
        txt: famille.txt(L, k, a, M),
        nature: famille.nature,
        famille: famille.id,
        niveau: famille.niveau,
        nbOperations: famille.nbOperations,
        nbVariables: famille.lettres,
        signes: famille.signes,
        code: famille.code,
        decimal: !!decimal,
    };
};

const ARTICLE = { somme: 'une somme', produit: 'un produit' };
const DERNIERE_OP = { somme: 'addition', produit: 'multiplication' };

/** Plusieurs expressions deux à deux distinctes, tirées dans les familles données. */
const tirerLot = (familles, nb, decimal) => {
    const vus = new Set();
    const lot = [];
    for (let essai = 0; essai < 120 && lot.length < nb; essai++) {
        const e = tirerExpr(randomChoice(familles), decimal);
        if (vus.has(e.txt)) continue;
        vus.add(e.txt);
        lot.push(e);
    }
    return lot.length === nb ? lot : null;
};

// ═════════ Carte 1 — Somme ou produit ? ═════════

const genNatureClasser = () => {
    const decimal = randomChoice([false, false, false, true]);   // réglage transverse
    for (let essai = 0; essai < 40; essai++) {
        const sommes = tirerLot(FAMILLES.filter(f => f.nature === 'somme'), 2, decimal);
        const produits = tirerLot(FAMILLES.filter(f => f.nature === 'produit'), 2, decimal);
        if (!sommes || !produits) continue;
        const tous = [...sommes, ...produits].map(e => e.txt);
        if (new Set(tous).size !== tous.length) continue;
        // points_de_vigilance 1 : DEUX entrées, et deux seulement
        const categories = shuffle([
            { name: 'Somme', items: sommes.map(e => mathChoix(e.txt)) },
            { name: 'Produit', items: produits.map(e => mathChoix(e.txt)) },
        ]);
        const payload = makeClasser(categories,
            '<p><b>Classe chaque expression selon sa nature.</b> Pense à la DERNIÈRE '
            + 'opération à effectuer (avec les règles de priorité).</p>',
            opts(L_NATURE));
        payload._meta.params = {
            decimal, categories,
            expressions: [...sommes, ...produits].map(e => ({ txt: e.txt, nature: e.nature, famille: e.famille })),
            niveau: Math.max(...[...sommes, ...produits].map(e => e.niveau)),
            nbOperations: Math.max(...[...sommes, ...produits].map(e => e.nbOperations)),
            nbVariables: Math.max(...[...sommes, ...produits].map(e => e.nbVariables)),
            erreurVisee: [...new Set([...sommes, ...produits].map(e => e.code))],
        };
        return payload;
    }
    // Repli déterministe
    const categories = [
        { name: 'Somme', items: ['3x + 5', '2x + 7x'].map(mathChoix) },
        { name: 'Produit', items: ['4(x + 6)', '5 × y'].map(mathChoix) },
    ];
    const payload = makeClasser(categories,
        '<p><b>Classe chaque expression selon sa nature.</b> Pense à la DERNIÈRE '
        + 'opération à effectuer (avec les règles de priorité).</p>',
        opts(L_NATURE));
    payload._meta.params = {
        decimal: false, categories,
        expressions: [
            { txt: '3x + 5', nature: 'somme', famille: 'sommeCoef' },
            { txt: '2x + 7x', nature: 'somme', famille: 'sommeDeuxCoef' },
            { txt: '4(x + 6)', nature: 'produit', famille: 'produitParenth' },
            { txt: '5 × y', nature: 'produit', famille: 'produitSimple' },
        ],
        niveau: 3, nbOperations: 3, nbVariables: 1,
        erreurVisee: ['juxtaposition_non_reconnue', 'somme_prise_pour_produit', 'produit_pris_pour_somme'],
    };
    return payload;
};

const genNatureParmiQcm = () => {
    const decimal = randomChoice([false, false, false, true]);
    for (let essai = 0; essai < 60; essai++) {
        const natureCible = randomChoice(['somme', 'produit']);
        const autre = natureCible === 'somme' ? 'produit' : 'somme';
        const cible = tirerLot(FAMILLES.filter(f => f.nature === natureCible), 1, decimal);
        const leurres = tirerLot(FAMILLES.filter(f => f.nature === autre), 3, decimal);
        if (!cible || !leurres) continue;
        const choix = [mathChoix(cible[0].txt), ...leurres.map(e => mathChoix(e.txt))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, mathChoix(cible[0].txt),
            `<p><b>Parmi ces expressions, laquelle est ${ARTICLE[natureCible]} ?</b> `
            + 'Cherche la dernière opération à effectuer.</p>',
            opts(L_NATURE));
        payload._meta.params = {
            natureCible, decimal, expression: cible[0].txt,
            niveau: Math.max(cible[0].niveau, ...leurres.map(e => e.niveau)),
            nbOperations: Math.max(cible[0].nbOperations, ...leurres.map(e => e.nbOperations)),
            nbVariables: Math.max(cible[0].nbVariables, ...leurres.map(e => e.nbVariables)),
            distracteurs: leurres.map(e => ({ code: e.code, valeur: e.txt })),
        };
        return payload;
    }
    // Repli déterministe
    const distracteurs = [
        { code: 'somme_prise_pour_produit', valeur: '3x + 5' },
        { code: 'juxtaposition_non_reconnue', valeur: '4y + 2' },
        { code: 'premiere_operation_lue', valeur: '2(x + 3) + 7' },
    ];
    const payload = makeQcm(
        [mathChoix('5(x + 6)'), ...distracteurs.map(d => mathChoix(d.valeur))], mathChoix('5(x + 6)'),
        '<p><b>Parmi ces expressions, laquelle est un produit ?</b> '
        + 'Cherche la dernière opération à effectuer.</p>', opts(L_NATURE));
    payload._meta.params = {
        natureCible: 'produit', decimal: false, expression: '5(x + 6)',
        niveau: 4, nbOperations: 3, nbVariables: 2, distracteurs,
    };
    return payload;
};

// ═════════ Carte 2 — Fiche d'identité ═════════

const FICHES = [
    {
        id: 'sommeCoef', nature: 'somme', niveau: 3, nbOperations: 2, nbVariables: 1,
        nom: 'A', parties: 2, motParties: 'termes',
        txt: (L, k, a) => `${k}${L} + ${a}`,
    },
    {
        id: 'produitParenth', nature: 'produit', niveau: 3, nbOperations: 2, nbVariables: 1,
        nom: 'B', parties: 2, motParties: 'facteurs',
        txt: (L, k, a) => `${k}(${L} + ${a})`,
    },
    {
        id: 'produitDeuxParenth', nature: 'produit', niveau: 4, nbOperations: 3, nbVariables: 2,
        nom: 'C', parties: 2, motParties: 'facteurs',
        txt: (L, k, a, M) => `(${L} + ${k})(${M} + ${a})`,
    },
    {
        id: 'sommeDeuxLettres', nature: 'somme', niveau: 4, nbOperations: 3, nbVariables: 2,
        nom: 'D', parties: 2, motParties: 'termes',
        txt: (L, k, a, M) => `${k}${L} + ${a}${M}`,
    },
];

const genFicheDrag = () => {
    const f = randomChoice(FICHES);
    const decimal = randomChoice([false, false, false, true]);
    const [L, M] = shuffle(LETTRES).slice(0, 2);
    const k = coefTexte(decimal);
    const a = coefTexte(decimal);
    const expr = f.txt(L, k, a, M);
    const motNature = f.nature === 'somme' ? 'somme' : 'produit';
    // Aucun calcul n'est demandé (exclusions 5) : la nature, la dernière
    // opération, et le nombre de termes ou de facteurs.
    const nom = `$${f.nom}$`;   // le nom de l'expression est une lettre : italique
    const phrase = `${nom} est ${f.nature === 'somme' ? 'une' : 'un'} {{${motNature}}}\n`
        + `La dernière opération à effectuer est une {{${DERNIERE_OP[f.nature]}}}\n`
        + `${nom} a {{${f.parties}}} ${f.motParties}`;
    const distracteurs = filtrerDistracteurs(phrase, shuffle([
        'somme', 'produit', 'addition', 'multiplication', 'soustraction', '3', '4',
    ])).slice(0, 4);
    const payload = makeTexteTrou(phrase,
        // Le nom de l'expression est une lettre lui aussi : il s'écrit en italique.
        // Les ÉTIQUETTES et les trous, eux, restent en texte brut : ce sont des mots
        // (« somme », « addition »), et le moteur du glisser-déposer découpe la phrase
        // sur les accolades, qu'aucune étiquette ne doit contenir.
        `<p><b>Voici la fiche d'identité de l'expression ${mathEnonce(`${f.nom} = ${expr}`)}.</b> `
        + 'Complète-la avec les bonnes étiquettes. Aucun calcul n\'est demandé.</p>',
        { ...opts(L_FICHE), mode: 'drag', distracteurs });
    payload._meta.params = {
        variante: f.id, nature: f.nature, expr, decimal,
        niveau: f.niveau, nbOperations: f.nbOperations, nbVariables: f.nbVariables,
        erreurVisee: ['derniere_et_premiere_operation', 'premiere_operation_lue'],
    };
    return payload;
};

// ═════════ Carte 3 — Vrai/faux et correction ═════════

/** Une affirmation, avec sa vérité par construction et le mot-clé qu'elle sert. */
const tirerAffirmation = (vraieVoulue, decimal) => {
    const genre = randomChoice(['nature', 'nature', 'derniereOp', 'valeur', 'premiereOp']);
    const e = tirerExpr(randomChoice(FAMILLES), decimal);
    if (genre === 'nature') {
        const natureDite = vraieVoulue ? e.nature : (e.nature === 'somme' ? 'produit' : 'somme');
        return {
            ligne: `${mathChoix(e.txt)} est ${ARTICLE[natureDite]}`,
            vraie: vraieVoulue,
            code: vraieVoulue ? null : e.code,
            expr: e,
        };
    }
    if (genre === 'derniereOp') {
        const opDite = vraieVoulue
            ? DERNIERE_OP[e.nature]
            : DERNIERE_OP[e.nature === 'somme' ? 'produit' : 'somme'];
        return {
            ligne: `Dans ${mathChoix(e.txt)}, la dernière opération à effectuer est une ${opDite}`,
            vraie: vraieVoulue,
            code: vraieVoulue ? null : 'derniere_et_premiere_operation',
            expr: e,
        };
    }
    if (genre === 'valeur') {
        // Toujours FAUSSE : la nature ne dépend pas de la valeur de la lettre
        if (vraieVoulue) return null;
        const v1 = randInt(VALEUR_ILLUSTRATIVE_MIN, 5);
        const v2 = randInt(6, VALEUR_ILLUSTRATIVE_MAX);
        const L = e.txt.match(/[xyztnm]/);
        if (!L) return null;
        return {
            ligne: `${mathChoix(e.txt)} est une somme quand $${L[0]}$ = ${v1} `
                + `et un produit quand $${L[0]}$ = ${v2}`,
            vraie: false,
            code: 'nature_dependante_de_la_valeur',
            expr: e,
        };
    }
    // Toujours FAUSSE : la nature ne se lit pas sur la PREMIÈRE opération
    if (vraieVoulue) return null;
    if (e.nature !== 'somme' || e.nbOperations < 2) return null;
    return {
        ligne: `${mathChoix(e.txt)} est un produit parce que la première opération `
            + 'à effectuer est une multiplication',
        vraie: false,
        code: 'premiere_operation_lue',
        expr: e,
    };
};

const genVf = () => {
    const decimal = randomChoice([false, false, false, true]);
    const lignes = [];
    const bonnes = [];
    const codes = [];
    const vues = new Set();
    const nbVrai = randInt(1, 3);
    const voulues = shuffle([
        ...Array(nbVrai).fill(true),
        ...Array(4 - nbVrai).fill(false),
    ]);
    let niveau = 1;
    let nbOperations = 1;
    let nbVariables = 1;
    voulues.forEach((vraieVoulue, iv) => {
        let ajoutee = false;
        for (let essai = 0; essai < 80; essai++) {
            const t = tirerAffirmation(vraieVoulue, decimal);
            if (!t || t.vraie !== vraieVoulue || vues.has(t.ligne)) continue;
            vues.add(t.ligne);
            lignes.push(t.ligne);
            bonnes.push(t.vraie ? 0 : 1);
            if (t.code) codes.push(t.code);
            niveau = Math.max(niveau, t.expr.niveau);
            nbOperations = Math.max(nbOperations, t.expr.nbOperations);
            nbVariables = Math.max(nbVariables, t.expr.nbVariables);
            ajoutee = true;
            break;
        }
        if (!ajoutee) {
            const k = 2 + iv;
            const ligne = vraieVoulue
                ? `${mathChoix(`${k}(y + 7)`)} est un produit`
                : `${mathChoix(`${k}y + 7y`)} est un produit`;
            if (!vues.has(ligne)) {
                vues.add(ligne);
                lignes.push(ligne);
                bonnes.push(vraieVoulue ? 0 : 1);
                if (!vraieVoulue) codes.push('somme_prise_pour_produit');
            }
        }
    });
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pense à la dernière opération à effectuer : '
        + 'c\'est elle qui donne sa nature à l\'expression.</p>',
        opts(L_VF));
    payload._meta.params = {
        lignes, bonnes, decimal, niveau, nbOperations, nbVariables,
        erreurVisee: [...new Set(codes)],
    };
    return payload;
};

const genCorrigeQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const cas = randomChoice(['prodPrisPourSomme', 'sommePrisePourProduit']);
        const [L] = shuffle(LETTRES);
        const k = coefTexte(false);
        const a = coefTexte(false);
        if (k === a) continue;
        let texte;
        let correct;
        let candidats;
        if (cas === 'prodPrisPourSomme') {
            texte = `<p><b>L'affirmation « ${mathEnonce(`${k}(${L} + ${a})`)} est une somme » `
                + 'est FAUSSE.</b> Quelle est la phrase correcte ?</p>';
            correct = `C'est un produit de deux facteurs : ${k} et ${mathChoix(`(${L} + ${a})`)}.`;
            candidats = [
                { valeur: `C'est une somme de deux termes : ${mathChoix(`${k}${L}`)} et ${a}.`, code: 'produit_pris_pour_somme' },
                { valeur: `C'est une somme, parce que la première opération à effectuer est l'addition de ${mathChoix(L)} et de ${a}.`, code: 'premiere_operation_lue' },
                { valeur: `C'est un produit quand ${mathChoix(L)} est grand, et une somme quand ${mathChoix(L)} est petit.`, code: 'nature_dependante_de_la_valeur' },
            ];
        } else {
            texte = `<p><b>L'affirmation « ${mathEnonce(`${k}${L} + ${a}${L}`)} est un produit » `
                + 'est FAUSSE.</b> Quelle est la phrase correcte ?</p>';
            correct = `C'est une somme de deux termes : ${mathChoix(`${k}${L}`)} et ${mathChoix(`${a}${L}`)}.`;
            candidats = [
                { valeur: `C'est un produit de deux facteurs : ${k} et ${a}.`, code: 'somme_prise_pour_produit' },
                { valeur: 'C\'est un produit, parce que la première opération à effectuer est une multiplication.', code: 'derniere_et_premiere_operation' },
                { valeur: `C'est une somme quand ${mathChoix(L)} = 2, et un produit quand ${mathChoix(L)} = 7.`, code: 'nature_dependante_de_la_valeur' },
            ];
        }
        const choix = [correct, ...candidats.map(c => c.valeur)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, correct, texte, opts(L_VF));
        payload._meta.params = {
            L, k, a, cas, niveau: 3, nbOperations: cas === 'prodPrisPourSomme' ? 2 : 3,
            nbVariables: 1, distracteurs: candidats,
        };
        return payload;
    }
    const candidats = [
        { valeur: `C'est une somme de deux termes : ${mathChoix('3x')} et 5.`, code: 'produit_pris_pour_somme' },
        { valeur: `C'est une somme, parce que la première opération à effectuer est l'addition de ${mathChoix('x')} et de 5.`, code: 'premiere_operation_lue' },
        { valeur: `C'est un produit quand ${mathChoix('x')} est grand, et une somme quand ${mathChoix('x')} est petit.`, code: 'nature_dependante_de_la_valeur' },
    ];
    const bon = `C'est un produit de deux facteurs : 3 et ${mathChoix('(x + 5)')}.`;
    const payload = makeQcm([bon, ...candidats.map(c => c.valeur)], bon,
        `<p><b>L'affirmation « ${mathEnonce('3(x + 5)')} est une somme » est FAUSSE.</b> `
        + 'Quelle est la phrase correcte ?</p>',
        opts(L_VF));
    payload._meta.params = {
        L: 'x', k: '3', a: '5', cas: 'prodPrisPourSomme', niveau: 3, nbOperations: 2,
        nbVariables: 1, distracteurs: candidats,
    };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    nature_classer: genNatureClasser,
    nature_parmi_qcm_unique: genNatureParmiQcm,
    fiche_texte_trou_drag: genFicheDrag,
    vf_tableau_vf: genVf,
    vf_qcm_unique: genCorrigeQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', parmi_qcm_unique: 'QCM', classer: 'Classer',
    tableau_vf: 'Vrai/Faux', texte_trou_drag: 'Drag',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'fiche', label: L_FICHE, formatsList: buildFormats('fiche') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
